import React, { useEffect, useState } from 'react'
import { Form, Select, Button, Card, Typography, Space, App, Alert } from 'antd'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useApi } from '../../services/ApiContext'
import { useCompanyStore } from '../../store/company/companyStore'
import { User } from '../../types/ffe'
import { apiClient } from '../../services/apiClient'
import { PaginatedResponse } from '../../services/types'

const { Title, Text } = Typography

interface Company {
  _id: string
  name: string
}

interface UtilsResponse {
  action: string
  result: Company[] | User[]
}

export const LoginAsUserPage: React.FC = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const api = useApi()
  const { message } = App.useApp()
  const user = useCompanyStore((state) => state.user)
  const [companies, setCompanies] = useState<Company[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    // Only allow global admins to access this page
    if (!user?.isGlobalAdmin) {
      navigate('/app/documents')
    } else {
      // Load companies when component mounts
      loadCompanies()
    }
  }, [user, navigate])

  const loadCompanies = async () => {
    try {
      setLoading(true)
      const response = await apiClient.post<UtilsResponse>('/utils', {
        action: 'listOrganisations'
      })
      setCompanies(response.result as Company[])
    } catch (error) {
      message.error('Failed to load companies')
    } finally {
      setLoading(false)
    }
  }

  const handleCompanyChange = async (companyId: string) => {
    try {
      setLoading(true)
      const response = await apiClient.post<UtilsResponse>('/utils', {
        action: 'listUsersInOrganisation',
        organisationId: companyId
      })
      setUsers(response.result as User[])
      form.setFieldValue('userId', undefined)
    } catch (error) {
      message.error('Failed to load users for this company')
    } finally {
      setLoading(false)
    }
  }

  const handleLoginAsUser = async (values: { userId: string }) => {
    try {
      setLoading(true)
      const selectedUser = users.find((u) => u._id === values.userId)
      if (!selectedUser) {
        throw new Error('User not found')
      }

      await api.loginAsUser(selectedUser.email)
      message.success(`Successfully logged in as ${selectedUser.firstName} ${selectedUser.lastName}`)
      navigate('/app/documents')
    } catch (error) {
      message.error('Failed to login as selected user')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start gap-2">
        <Button
          type="text"
          icon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate('/app/settings')}
        >
          Back to Settings
        </Button>
        <div>
          <Title level={4} className="!mb-0">
            Login as User
          </Title>
          <Text type="secondary">
            Select a company and user to login as
          </Text>
        </div>
      </div>

      <Alert
        message="Warning"
        description="You are about to login as another user. This action will log you out of your current session and log you in as the selected user. Please be careful with this feature."
        type="warning"
        showIcon
        className="mb-4"
      />

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleLoginAsUser}
        >
          <Form.Item
            name="companyId"
            label="Company"
            rules={[{ required: true, message: 'Please select a company' }]}
          >
            <Select
              placeholder="Select a company"
              onChange={handleCompanyChange}
              loading={loading}
              options={companies.map((company) => ({
                label: company.name,
                value: company._id
              }))}
            />
          </Form.Item>

          <Form.Item
            name="userId"
            label="User"
            rules={[{ required: true, message: 'Please select a user' }]}
          >
            <Select
              placeholder="Select a user"
              loading={loading}
              options={users.map((user) => ({
                label: `${user.firstName} ${user.lastName} (${user.email})`,
                value: user._id
              }))}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Login as Selected User
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  )
}
